import { isServer, html } from 'lit';
import { query, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { CrntElement } from '../../lib/crnt-element.js';
import { CrntBlurEvent } from '../../lib/events/blur.js';
import { CrntFocusEvent } from '../../lib/events/focus.js';
import { AnchorElementMixin } from '../anchor/anchor-element-mixin.js';
import { CrntButtonBaseMixin } from '../button-base/button-base-mixin.js';
import { ButtonElementMixin } from './button-element-mixin.js';

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
class CrntButton extends ButtonElementMixin(AnchorElementMixin(CrntButtonBaseMixin(CrntElement))) {
  constructor() {
    super(...arguments);
    /** Internals for form-associated custom elements. */
    this._internals = null;
    this.ariaLabel = null;
  }
  static {
    /** ==================================================================================================================
     * @region Internal logic
     ================================================================================================================== */
    /** @internal */
    this.formAssociated = true;
  }
  static {
    /** @internal */
    this.shadowRootOptions = {
      ...CrntElement.shadowRootOptions,
      delegatesFocus: true
    };
  }
  static {
    this.tagName = "crnt-button";
  }
  /** ==================================================================================================================
   * @region Programmatic events
   ================================================================================================================== */
  /** Removes focus from the container element. */
  blur() {
    if (this._container) {
      this._container.blur();
    } else {
      super.blur();
    }
  }
  /** Simulates a click on the button. */
  click() {
    if (this._container) {
      this._container.click();
    } else {
      super.click();
    }
  }
  /** Sets focus on the container element. */
  focus(options) {
    if (this._container) {
      this._container.focus(options);
    } else {
      super.focus(options);
    }
  }
  /** ==================================================================================================================
   * @region Events handlers
   ================================================================================================================== */
  _handleBlur() {
    this.dispatchEvent(new CrntBlurEvent());
  }
  _handleButtonClick(event) {
    if (this._isDisabled) {
      event.stopImmediatePropagation();
      return;
    }
    super.handleButtonClick();
  }
  _handleFocus() {
    this.dispatchEvent(new CrntFocusEvent());
  }
  /** ==================================================================================================================
   * @region Lifecycle events
   ================================================================================================================== */
  connectedCallback() {
    super.connectedCallback();
    if (!isServer) {
      this._internals = this.attachInternals();
      if (!this.isAnchor) {
        this._internals.role = "button";
      }
      this.addEventListener?.("click", this._handleButtonClick.bind(this));
    }
  }
  /** ==================================================================================================================
   * @region Render methods
   ================================================================================================================== */
  /** Render the component. */
  render() {
    const content = this._renderContent();
    return this.isAnchor ? this._renderAnchor(content) : this._renderButton(content);
  }
  /** Render the component as an anchor tag. */
  _renderAnchor(content) {
    return html`<a
			part="container"
			href=${ifDefined(this.href)}
			target=${this.target}
			aria-label=${ifDefined(this.ariaLabel ?? void 0)}
			aria-disabled=${ifDefined(this._isDisabled === true ? "true" : void 0)}
			@blur=${this._handleBlur}
			@focus=${this._handleFocus}
			>${content}</a
		>`;
  }
  /** Render the component as an button tag. */
  _renderButton(content) {
    return html`<button
			part="container"
			aria-disabled=${ifDefined(this._isDisabled === true ? "true" : void 0)}
			type=${this.type}
			aria-label=${ifDefined(this.ariaLabel ?? void 0)}
			@blur=${this._handleBlur}
			@focus=${this._handleFocus}
		>
			${content}
		</button>`;
  }
  /** Render the inner content of the button. */
  _renderContent() {
    return html`
			<span part="leading">
				<slot name="leading"></slot>
			</span>
			<span part="content">
				<slot></slot>
			</span>
			<span part="trailing">
				<slot name="trailing"></slot>
				<slot name="fixed-trailing"></slot>
			</span>
		`;
  }
}
__decorateClass([
  query('[part="container"]')
], CrntButton.prototype, "_container");
__decorateClass([
  property({ attribute: "aria-label" })
], CrntButton.prototype, "ariaLabel");

export { CrntButton };
